import { AxiosResponse } from "axios";
import api from "../Interceptors/Interceptor";
import ApiResponse from "../Models/ApiResponse";

export interface IAccessModuleForRole {
	code: string;
	nameTag: string;
	descriptionTag: string;
	moduleGroup: string;
	sequence: number;
	active: boolean;
}

export interface IRoleAccessModules {
	modules: IAccessModuleForRole[];
	legacyPreview: boolean;
	unmappedPermissionNames: string[];
}

export interface ICustomerAccessModules {
	modules: IAccessModuleForRole[];
}

export interface IAccessModuleToggle {
	code: string;
	active: boolean;
}

export type ModuleOverrideMode = "NONE" | "ADD" | "REMOVE";

export interface IAccessModuleForUser {
	code: string;
	nameTag: string;
	descriptionTag: string;
	moduleGroup: string;
	sequence: number;
	overrideMode: ModuleOverrideMode;
}

export interface IUserAccessModules {
	modules: IAccessModuleForUser[];
	activeOverrideCount: number;
}

export interface IAccessModuleOverride {
	code: string;
	overrideMode: ModuleOverrideMode;
}

export interface IAccessModuleCatalogEntry {
	code: string;
	nameTag: string;
	descriptionTag: string;
	moduleGroup: string;
	sequence: number;
}

export class AccessModuleService {

	public async getCatalog(): Promise<IAccessModuleCatalogEntry[]> {
		const response: AxiosResponse<ApiResponse<IAccessModuleCatalogEntry[]>> =
			await api.get("v1/access-module");
		return response.data.data ?? [];
	}

	public async getModulesForRole(roleUuid: string): Promise<IRoleAccessModules> {
		const response: AxiosResponse<ApiResponse<IRoleAccessModules>> =
			await api.get(`v1/security-role/${roleUuid}/access-module`);
		const data = response.data.data;
		return {
			modules: data?.modules ?? [],
			legacyPreview: data?.legacyPreview ?? false,
			unmappedPermissionNames: data?.unmappedPermissionNames ?? [],
		};
	}

	public async updateModulesForRole(
		roleUuid: string,
		toggles: IAccessModuleToggle[],
	): Promise<void> {
		await api.put(`v1/security-role/${roleUuid}/access-module`, toggles);
	}

	public async getModulesForCustomer(customerUuid: string): Promise<ICustomerAccessModules> {
		const response: AxiosResponse<ApiResponse<ICustomerAccessModules>> =
			await api.get(`v1/security-customer/${customerUuid}/access-module`);
		const data = response.data.data;
		return {
			modules: data?.modules ?? [],
		};
	}

	public async updateModulesForCustomer(
		customerUuid: string,
		toggles: IAccessModuleToggle[],
	): Promise<void> {
		await api.put(`v1/security-customer/${customerUuid}/access-module`, toggles);
	}

	public async getModulesForUser(userUuid: string): Promise<IUserAccessModules> {
		const response: AxiosResponse<ApiResponse<IUserAccessModules>> =
			await api.get(`v1/security-user/${userUuid}/access-module`);
		const data = response.data.data;
		return {
			modules: data?.modules ?? [],
			activeOverrideCount: data?.activeOverrideCount ?? 0,
		};
	}

	public async updateModulesForUser(
		userUuid: string,
		overrides: IAccessModuleOverride[],
	): Promise<void> {
		await api.put(`v1/security-user/${userUuid}/access-module`, overrides);
	}
}
